<template>
    <div :class="['field', 'radio-cards-field', { 'error': hasError, 'has-icons': hasIcons }]">
        <label class="label" v-if="label">{{ labelText }}</label>

        <div class="radio-cards">
            <label
                v-for="option in computedOptions"
                :key="option.value"
                class="radio-card"
                :class="{ 'is-selected': data[name] === option.value }"
            >
                <input
                    type="radio"
                    :id="`${id}-${option.value}`"
                    :name="name"
                    :value="option.value"
                    v-model="data[name]"
                    :disabled="disabled || option.disabled"
                    @change="emit"
                />
                <div class="card-icon" v-if="option.icon">
                    <i class="icon">{{ option.icon }}</i>
                </div>
                <div class="card-content">
                    <div class="card-title">{{ option.name }}</div>
                    <div class="card-description" v-if="option.description">{{ option.description }}</div>
                </div>
            </label>
        </div>

        <div class="hover-area"
             v-if="hasError"
             @mouseover="showErrorMessage"
             @mouseleave="hideErrorMessage">
            <div class="message-box">
                <div class="error-message" v-show="errorMessageVisible">
                    {{ errorMessage }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import InputHelper from '../../utils/InputHelper'

export default InputHelper.extend({
    name: 'RadioCards',
    props: {
        options: {
            type: Array,
            required: true
        },
        valuekey: {
            type: String,
            default: 'value'
        },
        namekey: {
            type: String,
            default: 'name'
        },
        descriptionkey: {
            type: String,
            default: 'description'
        },
        iconkey: {
            type: String,
            default: 'icon'
        }
    },
    computed: {
        computedOptions() {
            return this.options.map(option => ({
                value: option[this.valuekey],
                name: option[this.namekey],
                description: option[this.descriptionkey] || null,
                icon: option[this.iconkey] || null,
                disabled: option.disabled || false
            }))
        },
        hasIcons() {
            return this.computedOptions.some(option => option.icon)
        }
    },
    created() {
        this.$watch(`data.${this.name}`, value => {
            this.$emit('input', value)
        })
    }
})
</script>

<style lang="scss" scoped>
.radio-cards-field {
    .label {
        margin-bottom: 0.75rem;
    }

    // When icons are present, hide the radio buttons
    &.has-icons {
        .radio-card {
            input[type="radio"] {
                position: absolute;
                opacity: 0;
                pointer-events: none;
            }

            &.is-selected {
                background: #f0faf7;

                .card-icon {
                    background: #e0f5ef;

                    .icon {
                        color: #3BC173;
                    }
                }
            }
        }
    }
}

.radio-cards {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.radio-card {
    display: flex;
    cursor: pointer;
    padding: 1rem 1.25rem;
    align-items: flex-start;
    gap: 1.25rem;
    border: 1px solid #E5E5E5;
    background: #FFF;
    border-radius: 0.625rem;
    transition: all 0.2s ease;
    position: relative;

    &:hover:not(.is-disabled) {
        border-color: #b0b0b0;
        background: #fafafa;
    }

    &.is-selected {
        border-color: #3BC173;

        input[type="radio"] {
            &::before {
                background-color: #3BC173;
                transform: scale(1);
            }
        }
    }

    &.is-disabled {
        cursor: not-allowed;
        opacity: 0.6;
    }

    input[type="radio"] {
        flex-shrink: 0;
        width: 1.6rem;
        height: 1.6rem;
        align-self: center;
        margin: 0;
        padding: 0;
        appearance: none;
        border-radius: 50%;
        position: relative;
        box-shadow: 0 0 2px rgba(0, 0, 0, 0.3);
        cursor: inherit;

        &::before {
            content: '';
            position: absolute;
            top: 0.25rem;
            left: 0.25rem;
            width: 1.1rem;
            height: 1.1rem;
            border-radius: 50%;
            background-color: transparent;
            transform: scale(0);
            transition: transform 0.2s ease;
        }

        &:checked {
            border-color: #2CC36B;
        }

        &:disabled {
            cursor: not-allowed;
        }
    }

    .card-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 3rem;
        height: 3rem;
        background: #f5f5f5;
        border-radius: 0.375rem;
        flex-shrink: 0;
        transition: all 0.15s ease;

        .icon {
            font-size: 1.5rem;
            color: #9EA5A5;
            transition: color 0.15s ease;
        }
    }

    .card-content {
        display: flex;
        padding: 0 0.625rem 0 0;
        flex-direction: column;
        align-items: flex-start;
        flex: 1 0 0;
        align-self: stretch;
        justify-content: center;
    }

    .card-title {
        color: #1F1F1F;
        font-size: 1rem;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
    }

    .card-description {
        color: #6A7272;
        font-size: 0.875rem;
        font-style: normal;
        font-weight: 400;
        line-height: 1.4;
        margin-top: 0.25rem;
    }
}

.error {
    .radio-card {
        border-color: #ff3860;
    }
}
</style>
